import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Bot, Volume2 } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const VIDEOS = [
  {
    key: "luxe",
    src: `${MEDIA_BASE}/msdluxe-luxemeet.mp4`,
    poster: "/ai/ms-dluxe-2.jpg",
    file: "MsDLuxe-How-LuxeMeet-Works.mp4",
    titleKey: "msdluxe.luxeCard",
    accent: "border-amber-500/30",
    chip: "bg-amber-500/15 text-amber-300",
    chipLabel: "LuxeMeet",
    available: true,
  },
  {
    key: "dluxe",
    src: `${MEDIA_BASE}/msdluxe-dluxe.mp4`,
    poster: "/ai/ms-dluxe-1.jpg",
    file: "MsDLuxe-How-DLuxe-Works.mp4",
    titleKey: "msdluxe.dluxeCard",
    accent: "border-rose-500/30",
    chip: "bg-rose-500/15 text-rose-300",
    chipLabel: "DLuxe",
    available: false,
  },
];

export function MsDLuxeVideoGrid({ idPrefix = "msdluxe" }) {
  const { t } = useLocale();
  const [soundOn, setSoundOn] = useState(null);
  const refs = useRef({});

  const videos = VIDEOS.filter((v) => v.available);

  const enableSound = (key) => {
    const vid = refs.current[key];
    if (!vid) return;
    Object.entries(refs.current).forEach(([k, el]) => {
      if (k !== key && el) el.muted = true;
    });
    setSoundOn(key);
    vid.muted = false;
    vid.currentTime = 0;
    vid.loop = false;
    const p = vid.play();
    if (p) p.catch(() => {
      vid.muted = true;
      const p2 = vid.play();
      if (p2) p2.catch(() => {});
    });
  };

  const onEnded = (key) => {
    const vid = refs.current[key];
    if (vid) {
      vid.muted = true;
      vid.loop = true;
      const p = vid.play();
      if (p) p.catch(() => {});
    }
    setSoundOn((s) => (s === key ? null : s));
  };

  return (
    <div className={`grid gap-6 ${videos.length > 1 ? "md:grid-cols-2" : "max-w-sm mx-auto"}`} data-testid={`${idPrefix}-video-grid`}>
      {videos.map((v) => (
        <div key={v.key} className={`glass-card p-4 border ${v.accent}`} data-testid={`${idPrefix}-card-${v.key}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${v.chip}`}>{v.chipLabel}</span>
              <p className="font-semibold font-['Outfit'] text-sm">{t(v.titleKey)}</p>
            </div>
            <a
              href={`${v.src}?download=1`}
              download={v.file}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white transition-colors"
              data-testid={`${idPrefix}-download-${v.key}`}
            >
              <Download className="w-3.5 h-3.5" /> {t("msdluxe.download")}
            </a>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black">
            <video
              ref={(el) => { refs.current[v.key] = el; }}
              src={v.src}
              poster={v.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              controls
              controlsList="nodownload"
              onEnded={() => onEnded(v.key)}
              className="w-full aspect-[9/12] object-cover"
              data-testid={`${idPrefix}-video-${v.key}`}
            />
            {soundOn !== v.key && (
              <div className="absolute inset-x-0 bottom-14 flex justify-center pointer-events-none">
                <button
                  type="button"
                  onClick={() => enableSound(v.key)}
                  className="pointer-events-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/60 backdrop-blur-md border border-white/25 text-sm font-medium shadow-lg"
                  data-testid={`${idPrefix}-sound-btn-${v.key}`}
                >
                  <Volume2 className="w-4 h-4" /> {t("msdluxe.tapSound")}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MsDLuxeVideosDialog({ open, onOpenChange }) {
  const { t } = useLocale();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card border-white/10 max-h-[90vh] overflow-y-auto" data-testid="msdluxe-videos-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <Bot className="w-5 h-5 text-rose-400" /> {t("msdluxe.title1")}
          </DialogTitle>
          <DialogDescription>{t("msdluxe.desc")}</DialogDescription>
        </DialogHeader>
        {open && <MsDLuxeVideoGrid idPrefix="msdluxe-dlg" />}
        <p className="text-xs text-muted-foreground text-center">{t("msdluxe.share")}</p>
      </DialogContent>
    </Dialog>
  );
}
